import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Tabs,
  Tab,
  IconButton
} from '@mui/material';
import {
  TableChart as TableIcon,
  BarChart as ChartIcon,
  Description as SummaryIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const ResultsPanel = ({ result }) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleExport = () => {
    console.log('Export results:', result);
    // TODO: Implement export functionality
  };

  const renderTableData = (data) => (
    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {data.headers.map((header, index) => (
              <TableCell key={index} sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}>
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.rows.map((row, rowIndex) => (
            <TableRow key={rowIndex} hover>
              {row.map((cell, cellIndex) => (
                <TableCell key={cellIndex}>
                  {cellIndex === 0 ? (
                    <Chip label={cell} size="small" color="primary" variant="outlined" />
                  ) : (
                    cell
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderChartData = (data) => {
    if (data.type === 'bar') {
      return (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data.data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="value" fill="#8884d8" />
          </BarChart>
        </ResponsiveContainer>
      );
    } else if (data.type === 'pie') {
      return (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={data.data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {data.data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    return <Typography>Chart type not supported</Typography>;
  };

  const renderSummaryData = (data) => (
    <Box>
      {data.stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {data.stats.map((stat, index) => (
            <Grid item xs={6} md={4} key={index}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="primary">
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {data.examples && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Beispielabfragen:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {data.examples.map((example, index) => (
              <Chip
                key={index}
                label={example}
                variant="outlined"
                sx={{ justifyContent: 'flex-start' }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );

  const renderSuggestions = (suggestions) => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Weiterführende Fragen:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {suggestions.map((suggestion, index) => (
          <Chip
            key={index}
            label={suggestion}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Box>
    </Box>
  );

  if (!result) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          color: 'text.secondary'
        }}
      >
        <TableIcon sx={{ fontSize: 64, mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Ergebnisse werden hier angezeigt
        </Typography>
        <Typography variant="body2">
          Stellen Sie eine Frage, um OMOP-Daten zu erkunden
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Ergebnisse
        </Typography>
        <Box>
          <IconButton size="small" onClick={handleExport}>
            <ExportIcon />
          </IconButton>
          <IconButton size="small">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
        {result.result_type === 'table' && result.data && renderTableData(result.data)}

        {result.result_type === 'chart' && result.data && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {result.data.title}
            </Typography>
            {renderChartData(result.data)}
          </Box>
        )}

        {result.result_type === 'summary' && result.data && renderSummaryData(result.data)}

        {/* Suggestions */}
        {result.suggestions && renderSuggestions(result.suggestions)}
      </Box>
    </Box>
  );
};

export default ResultsPanel;